// Memberships & passes: the pure, unit-tested half of the admin pages
// (MEMBERSHIPS_PLAN.md §6, D31 wording, D32 length and payment, D39 status).
// Enum values are the API's JSON strings. Server-built copy (priceSummary,
// lengthSummary, whoCanJoin, statusLabel, renewsOrEndsLine) is shown as sent;
// what lives here is only what the editor needs BEFORE a save exists.
// Never "members only" (D31). Imported explicitly, never auto-imported.

export const MEMBERSHIP_LENGTH = Object.freeze({ongoing: 'Ongoing', setDates: 'SetDates', repeatingBlocks: 'RepeatingBlocks'});
export const TERM_UNIT = Object.freeze({week: 'Week', month: 'Month', quarter: 'Quarter', year: 'Year'});
export const INSTALMENTS = Object.freeze({none: 'None', monthly: 'Monthly', quarterly: 'Quarterly'});
export const REFUND_POLICY = Object.freeze({none: 'None', fullWithinDays: 'FullWithinDays', proRataUnused: 'ProRataUnused'});
export const AGE_RULE = Object.freeze({onJoinDate: 'OnJoinDate', onFixedDate: 'OnFixedDate'});
export const DISPLAY_AS = Object.freeze({membership: 'Membership', pass: 'Pass'});
export const TYPE_STATUS = Object.freeze({draft: 'Draft', live: 'Live', closedToNew: 'ClosedToNew'});
export const TYPE_VISIBILITY = Object.freeze({public: 'Public', hidden: 'Hidden'});
export const MID_PERIOD_PRICING = Object.freeze({full: 'Full', proRataMonths: 'ProRataMonths', proRataSessions: 'ProRataSessions'});
export const MEMBERSHIP_STATUS = Object.freeze({
  awaitingPayment: 'AwaitingPayment', active: 'Active', pastDue: 'PastDue', lapsed: 'Lapsed', cancelled: 'Cancelled', expired: 'Expired',
});
export const APPLICATION_STATUS = Object.freeze({
  pending: 'Pending', approved: 'Approved', declined: 'Declined', withdrawn: 'Withdrawn', expired: 'Expired', awaitingParent: 'AwaitingParent',
  awaitingLink: 'AwaitingLink',
});
export const SETTLEMENT = Object.freeze({paidOutside: 'PaidOutside', noCharge: 'NoCharge'});

/** Enum ints the Crud filters take (`filters[statuses][0]`), by JSON name. */
export const MEMBERSHIP_STATUS_INT = Object.freeze({AwaitingPayment: 0, Active: 1, PastDue: 2, Lapsed: 3, Cancelled: 4, Expired: 5});
export const APPLICATION_STATUS_INT = Object.freeze({Pending: 0, Approved: 1, Declined: 2, Withdrawn: 3, Expired: 4, AwaitingParent: 5, AwaitingLink: 6});
export const TYPE_STATUS_INT = Object.freeze({Draft: 0, Live: 1, ClosedToNew: 2});

/** D39: one status menu. Each entry is what POST {id}/status takes, with the words shown. */
export const TYPE_STATUS_OPTIONS = Object.freeze([
  {key: 'draft', status: TYPE_STATUS.draft, visibility: TYPE_VISIBILITY.public, label: 'Draft', hint: 'Nobody can see or join it yet.'},
  {key: 'live', status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.public, label: 'Live', hint: 'On the club page. People can join.'},
  {key: 'liveHidden', status: TYPE_STATUS.live, visibility: TYPE_VISIBILITY.hidden, label: 'Live, hidden', hint: 'Join by link or admin add only.'},
  {key: 'closedToNew', status: TYPE_STATUS.closedToNew, visibility: TYPE_VISIBILITY.public, label: 'Closed to new', hint: 'Current holders keep it and renew; nobody new can join.'},
]);

export function typeStatusKey(type) {
  if (!type) return 'draft';
  if (type.status === TYPE_STATUS.live) return type.visibility === TYPE_VISIBILITY.hidden ? 'liveHidden' : 'live';
  if (type.status === TYPE_STATUS.closedToNew) return 'closedToNew';
  return 'draft';
}

/** The chip tint for a type's status (D39): Live is green, hidden is brand, the rest neutral. */
export function typeStatusChipClass(type) {
  const key = typeStatusKey(type);
  if (key === 'live') return 'tl-chip--ok';
  if (key === 'liveHidden') return 'tl-chip--brand';
  return '';
}

export function isLiveType(type) {
  return !!type && type.status === TYPE_STATUS.live && !type.archivedAt;
}

/** "Membership" / "Pass" (D31 chip). */
export function displayAsLabel(displayAs) {
  return displayAs === DISPLAY_AS.pass ? 'Pass' : 'Membership';
}

/** "membership" / "pass" as a noun in a sentence. */
export function displayAsNoun(displayAs) {
  return displayAs === DISPLAY_AS.pass ? 'pass' : 'membership';
}

// ── Length and payment (D32) ───────────────────────────────────────────────

/** Mirrors MembershipRules.MonthsInTerm: weeks never count as months. */
export function monthsInTerm(termLength, termUnit) {
  const n = Number(termLength) || 0;
  if (termUnit === TERM_UNIT.month) return n;
  if (termUnit === TERM_UNIT.quarter) return 3 * n;
  if (termUnit === TERM_UNIT.year) return 12 * n;
  return 0;
}

/** Mirrors MembershipRules.InstalmentsAllowed: monthly needs two months, quarterly six. */
export function instalmentsAllowed(instalments, termLength, termUnit) {
  const months = monthsInTerm(termLength, termUnit);
  if (instalments === INSTALMENTS.monthly) return months >= 2;
  if (instalments === INSTALMENTS.quarterly) return months >= 6;
  return true;
}

/** Mirrors MembershipRules.InstalmentCount. */
export function instalmentCount(instalments, termLength, termUnit) {
  const months = monthsInTerm(termLength, termUnit);
  if (instalments === INSTALMENTS.monthly) return Math.max(1, months);
  if (instalments === INSTALMENTS.quarterly) return Math.max(1, Math.floor(months / 3));
  return 1;
}

/** One instalment, rounded up like the server (MembershipWording.InstalmentMinor). */
export function instalmentMinor(amountMinor, instalments, termLength, termUnit) {
  const count = instalmentCount(instalments, termLength, termUnit);
  return Math.ceil((Number(amountMinor) || 0) / count);
}

const UNIT_WORD = {Week: 'week', Month: 'month', Quarter: 'quarter', Year: 'year'};

/** Mirrors MembershipWording.TermPhrase: "a year", "every 3 months", "12 weeks", "each month", "the season", "the pass". */
export function termPhrase(type) {
  if (!type) return '';
  if (type.length === MEMBERSHIP_LENGTH.setDates) return type.displayAs === DISPLAY_AS.pass ? 'the pass' : 'the season';
  const length = Math.max(1, Number(type.termLength) || 1);
  const unit = UNIT_WORD[type.termUnit] ?? 'year';
  if (type.length === MEMBERSHIP_LENGTH.repeatingBlocks) return length === 1 ? `each ${unit}` : `${length} ${unit}s`;
  return length === 1 ? `a ${unit}` : `every ${length} ${unit}s`;
}

/** "Price for the year" / "Price for each block" / "Price for the pass": the label over a price box. */
export function priceBoxLabel(type) {
  if (!type) return 'Price';
  if (type.length === MEMBERSHIP_LENGTH.repeatingBlocks) return 'Price for each block';
  if (type.length === MEMBERSHIP_LENGTH.setDates) return type.displayAs === DISPLAY_AS.pass ? 'Price for the pass' : 'Price for the season';
  const length = Math.max(1, Number(type.termLength) || 1);
  const unit = UNIT_WORD[type.termUnit] ?? 'year';
  return length === 1 ? `Price for the ${unit}` : `Price for ${length} ${unit}s`;
}

/**
 * "They pay" beside a price row, before the server has a summary:
 * "£240 once a year" / "4 × £65" / "12 × £22" / "Free".
 */
export function theyPayLine(option, type, formatMoney) {
  const amount = Number(option?.amountMinor) || 0;
  if (amount <= 0) return 'Free';
  if (option.instalments === INSTALMENTS.monthly || option.instalments === INSTALMENTS.quarterly) {
    const count = instalmentCount(option.instalments, type?.termLength, type?.termUnit);
    return `${count} × ${formatMoney(instalmentMinor(amount, option.instalments, type?.termLength, type?.termUnit))}`;
  }
  if (type?.length === MEMBERSHIP_LENGTH.ongoing) return `${formatMoney(amount)} once ${termPhrase(type)}`;
  return `${formatMoney(amount)} once`;
}

/** The term units offered, with the "Each term" words. */
export const TERM_UNIT_OPTIONS = Object.freeze([
  {value: TERM_UNIT.week, title: 'Weeks'},
  {value: TERM_UNIT.month, title: 'Months'},
  {value: TERM_UNIT.quarter, title: 'Quarters'},
  {value: TERM_UNIT.year, title: 'Years'},
]);

// ── Who can join ───────────────────────────────────────────────────────────

/** The quick form's chips (D30) as an age band. */
export const WHO_CAN_JOIN_PRESETS = Object.freeze([
  {key: 'anyone', label: 'Anyone', minAge: null, maxAge: null},
  {key: 'adults', label: '18 and over', minAge: 18, maxAge: null},
  {key: 'under18', label: 'Under 18', minAge: null, maxAge: 17},
  {key: 'custom', label: 'Custom ages', minAge: null, maxAge: null},
]);

export function whoCanJoinPreset(minAge, maxAge) {
  if (minAge == null && maxAge == null) return 'anyone';
  if (minAge === 18 && maxAge == null) return 'adults';
  if (minAge == null && maxAge === 17) return 'under18';
  return 'custom';
}

/** Mirrors MembershipRules.WhoCanJoin for the editor's live preview. */
export function whoCanJoinText(minAge, maxAge) {
  if (minAge != null && maxAge != null) return `Ages ${minAge} to ${maxAge}`;
  if (minAge != null) return `${minAge} and over`;
  if (maxAge != null) return `Under ${Number(maxAge) + 1}`;
  return 'Anyone';
}

export const MONTH_OPTIONS = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
].map((title, i) => ({value: i + 1, title})));

/** "31 August" for the fixed-date age rule. */
export function fixedDateText(day, month) {
  const m = MONTH_OPTIONS.find(o => o.value === Number(month));
  if (!day || !m) return null;
  return `${day} ${m.title}`;
}

// ── Quick add (D30) ────────────────────────────────────────────────────────

/** The start-from chips, mirroring MembershipSuggestions.StartFrom (amounts are GBP starting points). */
export const START_FROM = Object.freeze([
  {key: 'senior', name: 'Senior', displayAs: DISPLAY_AS.membership, minAge: 18, maxAge: null, amountMinor: 24000, length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.year, requiresApproval: false},
  {key: 'junior', name: 'Junior', displayAs: DISPLAY_AS.membership, minAge: null, maxAge: 17, amountMinor: 6000, length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.year, requiresApproval: true},
  {key: 'social', name: 'Social', displayAs: DISPLAY_AS.membership, minAge: null, maxAge: null, amountMinor: 4000, length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.year, requiresApproval: false},
  {key: 'seasonPass', name: 'Season pass', displayAs: DISPLAY_AS.pass, minAge: null, maxAge: null, amountMinor: 6000, length: MEMBERSHIP_LENGTH.setDates, termLength: 12, termUnit: TERM_UNIT.week, requiresApproval: false},
  {key: 'blank', name: '', displayAs: DISPLAY_AS.membership, minAge: null, maxAge: null, amountMinor: null, length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.year, requiresApproval: false},
]);

/** A quick-add draft from a start-from chip (or a server suggestion, which has the same fields). */
export function quickAddDraft(start, currency = 'gbp') {
  const s = start ?? START_FROM[START_FROM.length - 1];
  const gbp = (currency || 'gbp').toLowerCase() === 'gbp';
  const amount = s.amountMinor ?? s.suggestedAmountMinor ?? null;
  return {
    startFrom: s.key ?? null,
    name: s.name ?? '',
    displayAs: s.displayAs ?? DISPLAY_AS.membership,
    minAge: s.minAge ?? null,
    maxAge: s.maxAge ?? null,
    amountMinor: gbp ? amount : null,
    length: s.length ?? MEMBERSHIP_LENGTH.ongoing,
    termLength: s.termLength ?? 1,
    termUnit: s.termUnit ?? TERM_UNIT.year,
    firstPeriodStartsOn: null,
    setDatesEndOn: null,
    requiresApproval: !!s.requiresApproval,
    showOnClubPage: true,
  };
}

/** The "How long it lasts" dropdown of the quick form: ongoing terms a club would actually pick. */
export const QUICK_LENGTH_OPTIONS = Object.freeze([
  {value: 'Ongoing|1|Year', title: 'Ongoing · renews every year', length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.year},
  {value: 'Ongoing|1|Month', title: 'Ongoing · renews every month', length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.month},
  {value: 'Ongoing|1|Quarter', title: 'Ongoing · renews every 3 months', length: MEMBERSHIP_LENGTH.ongoing, termLength: 1, termUnit: TERM_UNIT.quarter},
  {value: 'SetDates', title: 'Set dates · one term with a start and end', length: MEMBERSHIP_LENGTH.setDates, termLength: 1, termUnit: TERM_UNIT.year},
  {value: 'RepeatingBlocks|12|Week', title: 'Repeating 12-week blocks', length: MEMBERSHIP_LENGTH.repeatingBlocks, termLength: 12, termUnit: TERM_UNIT.week},
  {value: 'RepeatingBlocks|1|Month', title: 'Repeating monthly blocks', length: MEMBERSHIP_LENGTH.repeatingBlocks, termLength: 1, termUnit: TERM_UNIT.month},
  {value: 'RepeatingBlocks|3|Month', title: 'Repeating 3-month blocks', length: MEMBERSHIP_LENGTH.repeatingBlocks, termLength: 3, termUnit: TERM_UNIT.month},
  {value: 'RepeatingBlocks|1|Year', title: 'Repeating yearly blocks (a season)', length: MEMBERSHIP_LENGTH.repeatingBlocks, termLength: 1, termUnit: TERM_UNIT.year},
]);

export function quickLengthValue(draft) {
  if (!draft) return QUICK_LENGTH_OPTIONS[0].value;
  if (draft.length === MEMBERSHIP_LENGTH.setDates) return 'SetDates';
  const exact = QUICK_LENGTH_OPTIONS.find(o => o.length === draft.length && o.termLength === Number(draft.termLength) && o.termUnit === draft.termUnit);
  return exact?.value ?? QUICK_LENGTH_OPTIONS[0].value;
}

// ── Money boxes ────────────────────────────────────────────────────────────
// X62 (Grant 2026-10-19): money is ALWAYS shown to two decimals, in price boxes
// too. A box shows "6.50", "6.00", "12.00" once it loads a saved price or loses
// focus (MoneyField formats on blur / Enter / load, never mid-typing); an empty
// box stays empty where empty means free, and "0" reads "0.00" only where zero is
// a real price (the booking table's rows). The parser takes "6", "6.5", "6.50",
// "£6.50" and "6,50".

/**
 * A price box's text → minor units; empty = free (null). Accepts a currency
 * symbol, spaces and a comma: "6,50" is six pounds fifty (a comma with one or
 * two digits after it and no dot is the decimal point; "1,200" and "1,200.50"
 * are thousands). Negative or junk → null.
 */
export function majorToMinor(text) {
  if (text == null) return null;
  let s = String(text).replace(/[^0-9.,-]/g, '');
  if (s.trim() === '') return null;
  if (s.includes(',')) {
    s = /^[^.]*,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
  }
  const parsed = parseFloat(s);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

/**
 * Minor units → the text a price box shows, always two decimals ("6.00", "6.50").
 * null → "" (free / not set); 0 → "" where empty means free (the default), "0.00"
 * where zero is a real price (`zeroIsPrice`); negative → "".
 */
export function minorToMajor(minor, {zeroIsPrice = false} = {}) {
  if (minor == null || minor === '') return '';
  const n = Number(minor);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n === 0 && !zeroIsPrice) return '';
  return (n / 100).toFixed(2);
}

/**
 * The three rules of a price box (MoneyField wires them; unit-tested here):
 * typed → the text stays the user's own and the minor units follow at once;
 * settled (blur / Enter) → "6.50"; loaded (a value from outside) → "6.50"
 * unless the box has focus and the value is the echo of what it just typed.
 */
export function moneyBoxTyped(text) {
  const own = text == null ? '' : String(text);
  return {text: own, minor: majorToMinor(own)};
}

export function moneyBoxSettled(text, {zeroIsPrice = false} = {}) {
  return minorToMajor(majorToMinor(text), {zeroIsPrice});
}

export function moneyBoxLoaded(text, minor, {focused = false, zeroIsPrice = false} = {}) {
  if (focused && majorToMinor(text) === (minor ?? null)) return text;
  return minorToMajor(minor, {zeroIsPrice});
}

// ── Applications ───────────────────────────────────────────────────────────

/** "2 days left" / "Runs out today" / "Hold expired" for a card hold (D8); null without one. */
export function holdCountdown(preAuthExpiresAt, now = new Date()) {
  if (!preAuthExpiresAt) return null;
  const end = new Date(preAuthExpiresAt);
  if (Number.isNaN(end.getTime())) return null;
  const days = Math.floor((end.getTime() - now.getTime()) / 86400000);
  if (days < 0) return 'Hold expired';
  if (days === 0) return 'Runs out today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}

/** "3 days ago" / "today" for a queue row. */
export function agoText(dateLike, now = new Date()) {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '';
  // Calendar days in the viewer's zone: an application made yesterday evening is "1 day ago" this morning.
  const dayOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((dayOf(now) - dayOf(d)) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/** One answer as text (ValueJson is whatever the member sent: string, bool, number, array). */
export function answerText(valueJson) {
  if (valueJson == null || valueJson === '') return '–';
  let v = valueJson;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return v; }
  }
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '–';
  if (v && typeof v === 'object') return Object.values(v).join(' ');
  return String(v);
}

// ── Memberships (the people list) ─────────────────────────────────────────

/** The status chip for a membership row: words and tint. */
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "22 Oct" from a date-only value ("2026-10-22" / "2026-10-22T00:00:00") or a Date / instant (its local day). */
function shortDayMonth(v) {
  if (!v) return '';
  const ymd = typeof v === 'string' ? /^(\d{4})-(\d{2})-(\d{2})(T00:00:00(\.0+)?Z?)?$/.exec(v) : null;
  if (ymd) return `${parseInt(ymd[3], 10)} ${SHORT_MONTHS[parseInt(ymd[2], 10) - 1]}`;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d) ? '' : `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;
}

/**
 * R16-L4: a membership that hasn't started yet (given from a later date). X28 sends statusKey 'startsSoon' with
 * startsOn + statusLine ("Starts 22 Oct"); an older API is read from a current period that starts after now.
 */
export function membershipStartsLater(m, now = new Date()) {
  if (!m || m.status !== MEMBERSHIP_STATUS.active) return null;
  if (m.statusKey && m.statusKey !== 'startsSoon') return null;
  if (m.statusKey === 'startsSoon' || m.startsOn) return {startsOn: m.startsOn ?? m.currentPeriodStart ?? null, line: m.statusLine || `Starts ${shortDayMonth(m.startsOn ?? m.currentPeriodStart)}`};
  const start = m.currentPeriodStart ? new Date(m.currentPeriodStart) : null;
  if (!start || isNaN(start) || start <= now) return null;
  return {startsOn: m.currentPeriodStart, line: `Starts ${shortDayMonth(start)}`};
}

/**
 * R16-L4: given as No charge (every period waived). X28 sends noCharge / paidByKind 'free'; an older API is read from the
 * periods (the detail's, or the row's when it carries them).
 */
export function membershipNoCharge(m, detail = null) {
  if (!m) return false;
  if (typeof m.noCharge === 'boolean') return m.noCharge;
  if (m.paidByKind) return m.paidByKind === 'free';
  const periods = detail?.periods ?? m.periods ?? [];
  return periods.length > 0 && periods.every(p => p.status === 'Waived');
}

/**
 * R16-M1: an Introductory type renews as another type. The line that names the date, type and price ("Renews 18 October
 * 2027 as Senior Adult at £295 a year"), else the plain renews / ends line.
 */
export function membershipRenewsLine(m) {
  if (!m) return null;
  return m.renewsAsLine || m.renewsOrEndsLine || null;
}

/** R16-M1: "Then £295 a year as Senior Adult, renewed automatically." only when it renews into another type. */
export function membershipThenLine(m) {
  if (!m || !m.thenLine) return null;
  return m.renewsAsTypeName || m.renewsAsLine ? m.thenLine : null;
}

export function membershipStatusChip(m) {
  if (!m) return {label: '', cls: ''};
  switch (m.status) {
    case MEMBERSHIP_STATUS.active: {
      const later = m.cancelAtPeriodEnd ? null : membershipStartsLater(m);
      if (later) return {label: later.line, cls: 'tl-chip--brand'};
      return m.cancelAtPeriodEnd
          ? {label: 'Cancelled', cls: '', sub: 'Keeps it until the end date'}
          : {label: 'Active', cls: 'tl-chip--ok'};
    }
    case MEMBERSHIP_STATUS.awaitingPayment:
      return {label: 'Waiting for payment', cls: 'tl-chip--warn'};
    case MEMBERSHIP_STATUS.pastDue:
      return {label: 'Past due', cls: 'tl-chip--warn'};
    case MEMBERSHIP_STATUS.lapsed:
      return {label: 'Lapsed', cls: ''};
    case MEMBERSHIP_STATUS.cancelled:
      return {label: 'Cancelled', cls: ''};
    case MEMBERSHIP_STATUS.expired:
      return {label: 'Ended', cls: ''};
    default:
      return {label: m.status ?? '', cls: ''};
  }
}

/** The people page's tabs → Crud filters (status ints, current, renewsOn). */
export function peopleTabFilters(tab, now = new Date()) {
  switch (tab) {
    case 'current':
      return {current: true};
    case 'pastDue':
      return {statuses: [MEMBERSHIP_STATUS_INT.PastDue]};
    case 'renewing': {
      const pad = (n) => String(n).padStart(2, '0');
      const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const end = new Date(now.getTime() + 30 * 86400000);
      return {current: true, renewsOn: [iso(now) + 'T00:00:00', iso(end) + 'T23:59:59']};
    }
    case 'lapsed':
      return {statuses: [MEMBERSHIP_STATUS_INT.Lapsed, MEMBERSHIP_STATUS_INT.Expired, MEMBERSHIP_STATUS_INT.Cancelled]};
    default:
      return {};
  }
}

// ── Questions (the club's own) ─────────────────────────────────────────────

/** UserFormPromptFieldType is an int on the wire (no string converter on that enum). */
export const FIELD_TYPE = Object.freeze({textInput: 0, date: 1, dropdown: 2, checkbox: 3, numberPicker: 4, infoBlock: 5, consent: 6});

/** The field kinds offered in the editor, as the API's UserFormPromptFieldType plus LongAnswer. */
export const QUESTION_KINDS = Object.freeze([
  {key: 'short', title: 'Short answer', fieldType: FIELD_TYPE.textInput, longAnswer: false},
  {key: 'long', title: 'Long answer', fieldType: FIELD_TYPE.textInput, longAnswer: true},
  {key: 'choose', title: 'Choose one', fieldType: FIELD_TYPE.dropdown, longAnswer: false},
  {key: 'tick', title: 'Tick box', fieldType: FIELD_TYPE.checkbox, longAnswer: false},
  {key: 'agree', title: 'Tick to agree', fieldType: FIELD_TYPE.consent, longAnswer: false},
  {key: 'date', title: 'Date', fieldType: FIELD_TYPE.date, longAnswer: false},
  {key: 'number', title: 'Number', fieldType: FIELD_TYPE.numberPicker, longAnswer: false},
]);

export function questionKind(question) {
  if (!question) return 'short';
  const type = Number(question.fieldType);
  const found = QUESTION_KINDS.find(k => k.fieldType === type && (k.fieldType !== FIELD_TYPE.textInput || !!k.longAnswer === !!question.longAnswer));
  return found?.key ?? 'short';
}

export function questionKindTitle(question) {
  return QUESTION_KINDS.find(k => k.key === questionKind(question))?.title ?? 'Short answer';
}

/** OptionsJson ↔ one choice per line. */
export function optionsFromJson(optionsJson) {
  if (!optionsJson) return [];
  try {
    const parsed = JSON.parse(optionsJson);
    if (Array.isArray(parsed)) return parsed.map(String);
    if (parsed && typeof parsed === 'object') return Object.values(parsed).map(String);
  } catch { /* free text below */ }
  return String(optionsJson).split('\n').map(s => s.trim()).filter(Boolean);
}

export function optionsToJson(lines) {
  const list = (Array.isArray(lines) ? lines : String(lines ?? '').split('\n')).map(s => String(s).trim()).filter(Boolean);
  return list.length ? JSON.stringify(list) : null;
}

// ── Health questionnaire tweaks (D38) ──────────────────────────────────────

/** MedicalOverridesJson → {off: [keys], required: {key: bool}, added: [question]}. */
export function parseMedicalOverrides(json) {
  const empty = {off: [], required: {}, added: []};
  if (!json) return empty;
  try {
    const parsed = JSON.parse(json);
    return {
      off: Array.isArray(parsed?.off) ? parsed.off.map(String) : [],
      required: parsed?.required && typeof parsed.required === 'object' ? {...parsed.required} : {},
      added: Array.isArray(parsed?.added) ? parsed.added.map(a => ({...a})) : [],
    };
  } catch {
    return empty;
  }
}

export function serialiseMedicalOverrides(overrides) {
  const off = (overrides?.off ?? []).filter(Boolean);
  const required = Object.fromEntries(Object.entries(overrides?.required ?? {}).filter(([, v]) => typeof v === 'boolean'));
  const added = (overrides?.added ?? []).filter(a => a && String(a.label ?? '').trim());
  if (!off.length && !Object.keys(required).length && !added.length) return null;
  return JSON.stringify({off, required, added});
}

/** "11 questions: 1 added by your club, 1 turned off" for the health section line. */
export function healthQuestionsLine(questions, overrides) {
  const total = (questions ?? []).length;
  const added = (overrides?.added ?? []).length;
  const off = (overrides?.off ?? []).length;
  const parts = [];
  if (added) parts.push(`${added} added by your club`);
  if (off) parts.push(`${off} turned off`);
  const base = `${total} question${total === 1 ? '' : 's'}`;
  return parts.length ? `${base}: ${parts.join(', ')}` : base;
}

// ── Blocks (repeating, §4.1b) ──────────────────────────────────────────────

/** PriceOverridesJson → {optionId: minor}. */
export function parsePriceOverrides(json) {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? {...parsed} : {};
  } catch {
    return {};
  }
}

/** The block's words in the table: On sale / On sale from … / Skipped / Locked. */
export function blockStateLabel(block, onSaleDaysBefore, formatDate, now = new Date()) {
  if (!block) return '';
  if (block.skipped) return 'Skipped';
  if (block.onSale) return 'On sale';
  const start = new Date(block.startsOn);
  if (Number.isNaN(start.getTime())) return '';
  const from = new Date(start.getTime() - (Number(onSaleDaysBefore) || 0) * 86400000);
  if (start.getTime() < now.getTime()) return 'Ended';
  return `On sale from ${formatDate(from)}`;
}

// ── Payment states (the type editor's price section) ──────────────────────

/** What a paid type needs before it can go live, from the club's payment account (status 3 = ChargesEnabled). */
export function paymentsGateState(account, lookupFailed) {
  if (lookupFailed) return 'unknown';
  if (!account) return 'notStarted';
  return account.status === 3 ? 'enabled' : 'paused';
}

// ── Charges ────────────────────────────────────────────────────────────────

/** Line items that paid for (or held) a membership or pass: MembershipPeriod / MembershipApplication. */
export const isMembershipCharge = (charge) => (charge?.lineItems ?? []).some(li => /^membership/i.test(String(li?.sourceType ?? '')));

/**
 * Who a charge is from, as a list shows it. A nameless payer on a membership
 * charge is "Member" (D46 keeps member for holders); anywhere else "Player".
 */
export function chargePayerName(charge, {membership = false} = {}) {
  const name = String(charge?.userName ?? '').trim();
  const isMembership = membership || isMembershipCharge(charge);
  // An older API names every charge with no account "Payment link"; a membership's is the Member's (R15 sim N6).
  if (name && !(isMembership && name === 'Payment link')) return name;
  return isMembership ? 'Member' : 'Player';
}
